import { memo, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ActionMeta, InputActionMeta, MultiValue } from 'react-select';
import styled from '@emotion/styled';
import type { ProjectClips } from '@trinity/chat/types';
import { Button } from '../presentation/Button/Button';
import { CheckboxLabel } from '../presentation/Checkbox';
import {
  ProjectClipsFiltersContext,
  ProjectClipsGroupTagsContext,
  SetProjectClipsFiltersContext,
} from '../../contexts/project-clips/Context';
import type { GroupTagOption } from '../../contexts/project-clips/interfaces';
import { GroupTagsMultiSelect } from './GroupTagsMultiSelect';
import styles from './style/AdditionalFilters.module.css';

type Props = {
  onClose: () => void;
};

export const AdditionalFiltersMenu = memo(({ onClose }: Props) => {
  const filters = useContext(ProjectClipsFiltersContext);
  const setFilters = useContext(SetProjectClipsFiltersContext);

  const [groupTagIds, setGroupTagIds] = useState<number[]>(filters.groupTagIds);
  const [speakerRole, setSpeakerRole] = useState<ProjectClips.SpeakerFilter>(filters.speakerRole);

  const menuRef = useRef<HTMLDivElement>(null);

  const handleSpeakerRoleSelected = useCallback(
    (role: ProjectClips.SpeakerFilter) => () => {
      setSpeakerRole(role);
    },
    []
  );

  const handleSubmit = useCallback(() => {
    setFilters({ groupTagIds, speakerRole });
    onClose();
  }, [onClose, setFilters, groupTagIds, speakerRole]);

  const canApplySpeakersFilter = useMemo(() => filters.keyword.length > 0, [filters.keyword]);

  return (
    <div ref={menuRef} className={styles.menu}>
      <div className={styles.section}>
        <div className={styles.label}>Global Tags</div>
        <GroupTagsFilter menuRef={menuRef} value={groupTagIds} onChange={setGroupTagIds} />
      </div>

      <div className={styles.section}>
        <div className={styles.label}>Speaker</div>
        <div className={styles.speakers}>
          <CheckboxLabel
            className={styles.checkbox}
            disabled={!canApplySpeakersFilter}
            checked={speakerRole === 'all'}
            label="All"
            onChange={handleSpeakerRoleSelected('all')}
          />
          <CheckboxLabel
            className={styles.checkbox}
            disabled={!canApplySpeakersFilter}
            checked={speakerRole === 'respondent'}
            label="Respondent"
            onChange={handleSpeakerRoleSelected('respondent')}
          />
          <CheckboxLabel
            className={styles.checkbox}
            disabled={!canApplySpeakersFilter}
            checked={speakerRole === 'moderator'}
            label="Moderator"
            onChange={handleSpeakerRoleSelected('moderator')}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <Button className={styles.submit} variant="brick" onClick={handleSubmit}>
          Apply
        </Button>
      </div>
    </div>
  );
});

AdditionalFiltersMenu.displayName = 'ProjectClips.AdditionalFiltersMenu';

type GroupTagFilterProps = {
  value: number[];
  onChange: (value: number[]) => unknown;
  menuRef: React.RefObject<HTMLDivElement | null>;
};

const GroupTagsFilter = ({ menuRef, onChange, value }: GroupTagFilterProps) => {
  const groupTags = useContext(ProjectClipsGroupTagsContext);
  const [inputValue, setInputValue] = useState('');

  const handleInputChange = useCallback(
    (newValue: string, _meta: InputActionMeta) => setInputValue(newValue),
    []
  );

  const selected = useMemo(
    () => value.map((id) => groupTags.find((o) => o.id === id)).filter(Boolean) as GroupTagOption[],
    [groupTags, value]
  );

  const handleChange = useCallback(
    (val: MultiValue<GroupTagOption>, _meta: ActionMeta<GroupTagOption>) => {
      onChange(Array.from(val).map((v) => v.id));
    },
    [onChange]
  );

  const handleNoOptionsMessage = useCallback((obj: { inputValue: string }) => {
    return obj.inputValue?.length ? 'No tags found' : null;
  }, []);

  return (
    <GroupTagsMultiSelect
      inputValue={inputValue}
      menuPortalTarget={menuRef.current ?? undefined}
      noOptionsMessage={handleNoOptionsMessage}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={groupTags}
      selected={selected}
    />
  );
};

const Menu = styled.div`
  background: var(--white);
  border: 1px solid var(--gray-l);
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  padding: 12px;
  min-width: 240px;
`;

const Section = styled.div`
  margin-bottom: 12px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--gray-d);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
  border-top: 1px solid var(--gray-l);
`;
